/**
 * /payroll-l4/apprentice-programs — DOL RAPIDS / state apprentice program registry.
 *
 * Lists programs for the active entity. "+ New Program" opens a create dialog.
 * Each row has an Edit icon button. Programs link to DOL RAPIDS or state systems.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

import { api, errorMessage } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l4/apprentice-programs")({
  component: ApprenticeProgramsPage,
});

interface ApprenticeProgram {
  id: string;
  name: string;
  trade: string | null;
  dol_rapids_id: string | null;
  state_program_id: string | null;
  state_code: string | null;
  sponsor_name: string | null;
  ratio_journey_to_apprentice: number | null;
  period_count: number | null;
  period_length_months: number | null;
  progression_pct_table: Record<string, number>;
  is_active: boolean;
  registered_date: string | null;
  expiration_date: string | null;
  notes: string | null;
}

const BLANK_CREATE = {
  name: "",
  dol_rapids_id: "",
  state_program_id: "",
  state_code: "",
  trade: "",
  sponsor_name: "",
  ratio_journey_to_apprentice: "",
  period_count: "",
  period_length_months: "",
  registered_date: "",
  expiration_date: "",
  notes: "",
};

const BLANK_EDIT = {
  name: "",
  is_active: true,
  notes: "",
  expiration_date: "",
};

function ApprenticeProgramsPage() {
  const qc = useQueryClient();
  const [activeOnly, setActiveOnly] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editProgram, setEditProgram] = useState<ApprenticeProgram | null>(null);
  const [createForm, setCreateForm] = useState(BLANK_CREATE);
  const [editForm, setEditForm] = useState(BLANK_EDIT);

  const { data: programs = [], isLoading, error } = useQuery({
    queryKey: ["payroll-l4", "apprentice-programs", activeOnly],
    queryFn: async () => {
      const url = `/api/payroll-l4/apprentice-programs?active_only=${activeOnly}`;
      return (await api(url)) as ApprenticeProgram[];
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        name: createForm.name.trim(),
      };
      if (createForm.dol_rapids_id.trim()) payload.dol_rapids_id = createForm.dol_rapids_id.trim();
      if (createForm.state_program_id.trim()) payload.state_program_id = createForm.state_program_id.trim();
      if (createForm.state_code.trim()) payload.state_code = createForm.state_code.trim().toUpperCase();
      if (createForm.trade.trim()) payload.trade = createForm.trade.trim();
      if (createForm.sponsor_name.trim()) payload.sponsor_name = createForm.sponsor_name.trim();
      if (createForm.ratio_journey_to_apprentice.trim())
        payload.ratio_journey_to_apprentice = parseFloat(createForm.ratio_journey_to_apprentice);
      if (createForm.period_count.trim()) payload.period_count = parseInt(createForm.period_count, 10);
      if (createForm.period_length_months.trim())
        payload.period_length_months = parseInt(createForm.period_length_months, 10);
      if (createForm.registered_date) payload.registered_date = createForm.registered_date;
      if (createForm.expiration_date) payload.expiration_date = createForm.expiration_date;
      if (createForm.notes.trim()) payload.notes = createForm.notes.trim();
      return api("/api/payroll-l4/apprentice-programs", { method: "POST", body: payload });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l4", "apprentice-programs"] });
      setCreateOpen(false);
      setCreateForm(BLANK_CREATE);
    },
  });

  const update = useMutation({
    mutationFn: async () => {
      if (!editProgram) return;
      const payload: Record<string, unknown> = {};
      if (editForm.name.trim()) payload.name = editForm.name.trim();
      payload.is_active = editForm.is_active;
      if (editForm.notes.trim()) payload.notes = editForm.notes.trim();
      if (editForm.expiration_date) payload.expiration_date = editForm.expiration_date;
      return api(`/api/payroll-l4/apprentice-programs/${editProgram.id}`, {
        method: "PATCH",
        body: payload,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l4", "apprentice-programs"] });
      setEditProgram(null);
    },
  });

  function openEdit(prog: ApprenticeProgram) {
    setEditProgram(prog);
    setEditForm({
      name: prog.name,
      is_active: prog.is_active,
      notes: prog.notes ?? "",
      expiration_date: prog.expiration_date ?? "",
    });
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Apprentice Programs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            DOL RAPIDS (the federal apprentice-program registry) and state-registered
            apprentice programs. Workers classified as apprentices on Davis-Bacon
            projects must be enrolled in a registered program.
          </Typography>
          <Box sx={{ mt: 1 }}>
            <HelpHint docPath="payroll-l4/apprentice-programs.md" label="Apprentice Programs help" />
          </Box>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="outlined"
            size="small"
            onClick={() => setActiveOnly((v) => !v)}
          >
            {activeOnly ? "Show All" : "Active Only"}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            New Program
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage(error)}
        </Alert>
      )}

      {isLoading ? (
        <CircularProgress />
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Trade</TableCell>
                <TableCell>DOL RAPIDS ID</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Ratio (J:A)</TableCell>
                <TableCell>Periods</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Active</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {programs.map((prog) => (
                <TableRow key={prog.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {prog.name}
                    </Typography>
                    {prog.sponsor_name && (
                      <Typography variant="caption" color="text.secondary">
                        {prog.sponsor_name}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{prog.trade ?? "—"}</TableCell>
                  <TableCell>
                    {prog.dol_rapids_id ? (
                      <Typography variant="body2" fontFamily="monospace">
                        {prog.dol_rapids_id}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {prog.state_program_id ?? "—"}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {prog.state_code ? <Chip size="small" label={prog.state_code} /> : "—"}
                  </TableCell>
                  <TableCell>
                    {prog.ratio_journey_to_apprentice != null
                      ? `${prog.ratio_journey_to_apprentice}:1`
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {prog.period_count != null ? (
                      <>
                        {prog.period_count}
                        {prog.period_length_months
                          ? ` × ${prog.period_length_months}mo`
                          : ""}
                      </>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>{prog.expiration_date ?? "—"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={prog.is_active ? "Active" : "Inactive"}
                      color={prog.is_active ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => openEdit(prog)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {programs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No programs yet. Click <strong>+ New Program</strong> to add one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* ── Create dialog ── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Apprentice Program</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 1 }}>
            <TextField
              label="Program Name" required size="small" value={createForm.name}
              onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="IBEW Local 11 — Wireman"
              sx={{ gridColumn: "span 2" }}
            />
            <TextField
              label="DOL RAPIDS ID" size="small" value={createForm.dol_rapids_id}
              onChange={(e) => setCreateForm((f) => ({ ...f, dol_rapids_id: e.target.value }))}
              placeholder="0000CA"
              helperText="ID from the federal DOL RAPIDS registry, if the program is federally registered."
            />
            <TextField
              label="State Program ID" size="small" value={createForm.state_program_id}
              onChange={(e) => setCreateForm((f) => ({ ...f, state_program_id: e.target.value }))}
              placeholder="CA-APP-12345"
              helperText="Your state apprenticeship agency's program ID. Use whichever applies."
            />
            <TextField
              label="State" size="small" value={createForm.state_code}
              onChange={(e) => setCreateForm((f) => ({ ...f, state_code: e.target.value }))}
              inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
              placeholder="CA"
            />
            <TextField
              label="Trade" size="small" value={createForm.trade}
              onChange={(e) => setCreateForm((f) => ({ ...f, trade: e.target.value }))}
              placeholder="Electrician"
            />
            <TextField
              label="Sponsor / Joint Committee Name" size="small" value={createForm.sponsor_name}
              onChange={(e) => setCreateForm((f) => ({ ...f, sponsor_name: e.target.value }))}
              sx={{ gridColumn: "span 2" }}
            />
            <TextField
              label="Journey:Apprentice Ratio" size="small" type="number"
              value={createForm.ratio_journey_to_apprentice}
              onChange={(e) => setCreateForm((f) => ({ ...f, ratio_journey_to_apprentice: e.target.value }))}
              placeholder="3"
              helperText="e.g. 3 means 3 journeyworkers per apprentice"
            />
            <TextField
              label="Period Count" size="small" type="number"
              value={createForm.period_count}
              onChange={(e) => setCreateForm((f) => ({ ...f, period_count: e.target.value }))}
              placeholder="8"
            />
            <TextField
              label="Period Length (months)" size="small" type="number"
              value={createForm.period_length_months}
              onChange={(e) => setCreateForm((f) => ({ ...f, period_length_months: e.target.value }))}
              placeholder="6"
            />
            <TextField
              label="Registered Date" type="date" size="small" value={createForm.registered_date}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setCreateForm((f) => ({ ...f, registered_date: e.target.value }))}
            />
            <TextField
              label="Expiration Date" type="date" size="small" value={createForm.expiration_date}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setCreateForm((f) => ({ ...f, expiration_date: e.target.value }))}
              helperText="Leave blank if no expiration"
            />
            <TextField
              label="Notes" size="small" multiline rows={2} value={createForm.notes}
              onChange={(e) => setCreateForm((f) => ({ ...f, notes: e.target.value }))}
              sx={{ gridColumn: "span 2" }}
            />
          </Box>
          {create.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage(create.error)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateOpen(false); setCreateForm(BLANK_CREATE); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!createForm.name.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? <CircularProgress size={18} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit dialog ── */}
      <Dialog open={editProgram !== null} onClose={() => setEditProgram(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Program</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 1 }}>
            <TextField
              label="Program Name" required size="small" value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              sx={{ gridColumn: "span 2" }}
            />
            <TextField
              label="Expiration Date" type="date" size="small" value={editForm.expiration_date}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setEditForm((f) => ({ ...f, expiration_date: e.target.value }))}
              helperText="Clear to remove expiration"
            />
            <TextField
              select size="small" label="Status" value={editForm.is_active ? "active" : "inactive"}
              onChange={(e) => setEditForm((f) => ({ ...f, is_active: e.target.value === "active" }))}
              SelectProps={{ native: true }}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </TextField>
            <TextField
              label="Notes" size="small" multiline rows={3} value={editForm.notes}
              onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
              sx={{ gridColumn: "span 2" }}
            />
          </Box>
          {update.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage(update.error)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditProgram(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!editForm.name.trim() || update.isPending}
            onClick={() => update.mutate()}
          >
            {update.isPending ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
